import { Check } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useMobileLayout } from '@/utils/useMobileLayout';
import { cn } from '@/lib/utils';

export interface WizardStep {
  id: string;
  label: string;
}

interface WizardStepperProps {
  steps: WizardStep[];
  currentStep: number; // zero-indexed
  onStepClick?: (index: number) => void;
  className?: string;
}

export function WizardStepper({
  steps,
  currentStep,
  onStepClick,
  className,
}: WizardStepperProps) {
  const { isMobile } = useMobileLayout();
  const total = steps.length;
  const safeCurrent = Math.max(0, Math.min(currentStep, total - 1));
  const progressValue = total > 1 ? ((safeCurrent + 1) / total) * 100 : 0;

  if (isMobile) {
    const current = steps[safeCurrent];
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">
            Step {safeCurrent + 1} of {total}
          </span>
          {current && <span className="text-muted-foreground">{current.label}</span>}
        </div>
        <Progress value={progressValue} className="h-2" />
      </div>
    );
  }

  return (
    <ol className={cn('flex items-center gap-2', className)}>
      {steps.map((step, index) => {
        const isComplete = index < safeCurrent;
        const isCurrent = index === safeCurrent;
        const clickable = Boolean(onStepClick) && index <= safeCurrent;
        return (
          <li key={step.id} className="flex flex-1 items-center gap-2">
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick?.(index)}
              className={cn(
                'flex items-center gap-2 rounded-md px-2 py-1 text-sm transition',
                clickable && 'hover:bg-muted',
                !clickable && 'cursor-default',
              )}
            >
              <span
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium',
                  isComplete && 'bg-primary text-primary-foreground',
                  isCurrent && 'bg-primary/15 text-primary ring-2 ring-primary',
                  !isComplete && !isCurrent && 'bg-muted text-muted-foreground',
                )}
              >
                {isComplete ? <Check className="h-4 w-4" /> : index + 1}
              </span>
              <span
                className={cn(
                  'whitespace-nowrap',
                  isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </button>
            {index < total - 1 && (
              <div
                className={cn(
                  'h-px flex-1',
                  isComplete ? 'bg-primary' : 'bg-border',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default WizardStepper;
